"use client";

import React, { useRef, useEffect, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { KeyboardArrowDown } from "@mui/icons-material";
import { colors, spacing, radius, shadows, layout } from "@/ui/tokens";
import { Avatar } from "@/ui";
import { SolaMessage } from "./Message";
import { useChatStore } from "@/lib/sola/store";
import type { SolaMessage as SolaMessageType } from "@/lib/sola/types";
import mdStyles from "./Message.module.css";

interface MessageListProps {
  messageIds: string[];
  isLoading?: boolean;
  liveMessages?: SolaMessageType[];
}

export function MessageList({ messageIds, isLoading = false, liveMessages }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Detect if user has scrolled up (don't fight them)
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isScrolledUp = distanceFromBottom > 80;
    userScrolledUpRef.current = isScrolledUp;
    setShowScrollButton(isScrolledUp);
  }, []);

  const scrollToBottom = useCallback((force?: boolean, smooth?: boolean) => {
    if (force || !userScrolledUpRef.current) {
      const el = scrollContainerRef.current;
      if (el) {
        if (smooth) {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      }
      userScrolledUpRef.current = false;
      setShowScrollButton(false);
    }
  }, []);

  // Scroll on new messages (smooth). This effect drives the scroll container
  // (an external DOM system); hiding the scroll-to-bottom button is part of
  // resetting that scroll UI after auto-scrolling, so the setState is intentional.
  useEffect(() => {
    userScrolledUpRef.current = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShowScrollButton(false);
    scrollToBottom(false, true);
  }, [messageIds.length, scrollToBottom]);

  // Scroll during streaming as content grows (instant to avoid competing animations)
  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }
  }, [isLoading, liveMessages, scrollToBottom]);

  // Keep scroll pinned when content height changes after streaming finishes
  // (e.g., tool thinking states resolving to final height)
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (!userScrolledUpRef.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    // Observe the inner content container
    const inner = el.firstElementChild;
    if (inner) observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  // Show an immediate "thinking" indicator while we wait for SOLA's response.
  // The assistant message isn't created until the first stream chunk arrives,
  // so until then the last message is still the user's. This bridges that gap
  // (and covers hidden suggested-action prompts, which render nothing).
  const lastMessage = liveMessages?.[liveMessages.length - 1];
  const awaitingResponse = isLoading && lastMessage?.role === "user";

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          height: "100%",
          overflow: "auto",
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          paddingTop: spacing[5],
          paddingBottom: spacing[5],
        }}
      >
        <div style={{ maxWidth: layout.chatColumnWidth, margin: "0 auto" }}>
          {messageIds.map((messageId, index) => {
            // Use live messages during streaming for real-time updates
            const message = liveMessages
              ? liveMessages.find((m) => m.id === messageId)
              : useChatStore.getState().messagesById[messageId];

            if (!message) return null;

            // Hidden messages (e.g. long suggested-action prompts) are sent to
            // the model but never rendered, so the user sees SOLA's reply first.
            if ((message.metadata as { hidden?: boolean } | undefined)?.hidden) {
              return null;
            }

            // Mark the last message as loading if still streaming
            const isLastMessage = index === messageIds.length - 1;
            const showLoading = isLoading && isLastMessage;

            // Only show suggestion pills on the last message, after streaming is done
            const showSuggestions = isLastMessage && !isLoading;

            return (
              <div key={messageId} style={{ marginBottom: spacing[5] }}>
                <SolaMessage message={message} isLoading={showLoading} showSuggestions={showSuggestions} />
              </div>
            );
          })}

          {awaitingResponse && (
            <div style={{ marginBottom: spacing[5] }}>
              <div
                style={{
                  display: "flex",
                  gap: spacing[4],
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                <Avatar size="sm" name="SOLA" src="/img/floats/sola.svg" />
                <div
                  style={{
                    padding: spacing[4],
                    backgroundColor: colors.canvas.default,
                    border: `1px solid ${colors.border.subtle}`,
                    borderRadius: radius.lg,
                    borderTopLeftRadius: radius.sm,
                    boxShadow: shadows[1],
                  }}
                >
                  <div className={mdStyles.typingIndicator}>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      <AnimatePresence>
        {showScrollButton && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.2 }}
            onClick={() => scrollToBottom(true, true)}
            aria-label="Scroll to bottom"
            style={{
              position: "absolute",
              bottom: spacing[4],
              left: "50%",
              transform: "translateX(-50%)",
              width: 36,
              height: 36,
              borderRadius: radius.pill,
              border: `1px solid ${colors.border.subtle}`,
              backgroundColor: colors.canvas.default,
              boxShadow: shadows[2],
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.text.muted,
              zIndex: 10,
            }}
          >
            <KeyboardArrowDown style={{ fontSize: 20 }} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
